import { Matrix4, Object3D, Vector2, Vector3 } from 'three';

class CSS2DObject extends Object3D {
  constructor(element = document.createElement('div')) {
    super();

    this.isCSS2DObject = true;

    this.element = element;

    this.element.style.position = 'absolute';
    this.element.style.userSelect = 'none';

    this.element.setAttribute('draggable', false);

    this.center = new Vector2(0.5, 0.5); // ( 0, 0 ) is the lower left; ( 1, 1 ) is the top right

    this.addEventListener('removed', function () {
      this.traverse(function (object) {
        if (object.element instanceof Element && object.element.parentNode !== null) {
          object.element.parentNode.removeChild(object.element);
        }
      });
    });
  }

  copy(source, recursive) {
    super.copy(source, recursive);

    this.element = source.element.cloneNode(true);

    this.center = source.center;

    return this;
  }
}

const _vector = new Vector3();
const _viewMatrix = new Matrix4();
const _viewProjectionMatrix = new Matrix4();
const _a = new Vector3();
const _b = new Vector3();

class CSS2DRenderer {
  constructor(parameters = {}) {
    const _this = this;

    let _width, _height;
    let _widthHalf, _heightHalf;

    const cache = {
      objects: new WeakMap(),
    };

    const domElement = parameters.element !== undefined ? parameters.element : document.createElement('div');
    domElement.style.overflow = 'hidden';
    this.domElement = domElement;

    const manualMaxDepth = typeof parameters.manualMaxDepth === 'number' ? Math.max(parameters.manualMaxDepth, 1) : 1;
    this.manualMaxDepth = manualMaxDepth;

    this.getSize = function () {
      return {
        width: _width,
        height: _height,
      };
    };

    this.render = function (scene, camera) {
      if (scene.matrixWorldAutoUpdate === true) scene.updateMatrixWorld();
      if (camera.parent === null && camera.matrixWorldAutoUpdate === true) camera.updateMatrixWorld();

      _viewMatrix.copy(camera.matrixWorldInverse);
      _viewProjectionMatrix.multiplyMatrices(camera.projectionMatrix, _viewMatrix);

      renderObject(scene, scene, camera, scene.visible);
      zOrder(scene);
    };

    this.setSize = function (width, height) {
      _width = width;
      _height = height;

      _widthHalf = _width / 2;
      _heightHalf = _height / 2;

      this.setDisplayArea(width, height);
    };

    this.setDisplayArea = function (width, height) {
      domElement.style.width = width + 'px';
      domElement.style.height = height + 'px';
    };

    this.clear = function () {
      this.domElement.replaceChildren();
    };

    /**
     * 2D dom 元素的渲染函数
     * @param {Object3D} object 渲染对象
     * @param {Scene} scene 场景
     * @param {Camera} camera 相机
     * @param {boolean} parentVisible 父元素的显隐
     * @returns
     */
    function renderObject(object, scene, camera, parentVisible) {
      // 若当前节点的图层与相机图层不一致，则忽略当前节点及其所有子节点
      if (object.layers.test(camera.layers) !== true) return;

      if (object.isCSS2DObject) {
        // 若 dom 节点已被手动插入到其它位置，则忽略当前节点及其所有子节点
        if (object.element.parentNode !== null && !has(object.element)) return;

        // 当且仅当 dom 节点未被插入时，将 dom 节点插入到默认根节点中
        if (object.element.parentNode === null) {
          domElement.appendChild(object.element);
        }

        _vector.setFromMatrixPosition(object.matrixWorld);
        _vector.applyMatrix4(_viewProjectionMatrix);

        const visible = object.visible === true && parentVisible === true && _vector.z >= -1 && _vector.z <= 1;
        object.element.style.display = visible === true ? '' : 'none';

        if (visible === true) {
          object.onBeforeRender(_this, scene, camera);

          const element = object.element;

          element.style.transform =
            'translate(' +
            -100 * object.center.x +
            '%,' +
            -100 * object.center.y +
            '%)' +
            'translate(' +
            (_vector.x * _widthHalf + _widthHalf) +
            'px,' +
            (-_vector.y * _heightHalf + _heightHalf) +
            'px)';

          object.onAfterRender(_this, scene, camera);
        }

        const objectData = {
          distanceToCameraSquared: getDistanceToSquared(camera, object),
        };

        cache.objects.set(object, objectData);
      }

      for (let i = 0, l = object.children.length; i < l; i++) {
        renderObject(object.children[i], scene, camera, parentVisible && object.visible);
      }
    }

    function getDistanceToSquared(object1, object2) {
      _a.setFromMatrixPosition(object1.matrixWorld);
      _b.setFromMatrixPosition(object2.matrixWorld);

      return _a.distanceToSquared(_b);
    }

    function filterAndFlatten(scene) {
      const result = [];

      scene.traverse(function (object) {
        if (object.isCSS2DObject) result.push(object);
      });

      return result;
    }

    function zOrder(scene) {
      const sorted = filterAndFlatten(scene).sort(function (a, b) {
        if (a.renderOrder !== b.renderOrder) {
          return b.renderOrder - a.renderOrder;
        }

        const distanceA = cache.objects.get(a)?.distanceToCameraSquared ?? 0;
        const distanceB = cache.objects.get(b)?.distanceToCameraSquared ?? 1;

        return distanceA - distanceB;
      });

      const zMax = sorted.length;

      for (let i = 0, l = sorted.length; i < l; i++) {
        sorted[i].element.style.zIndex = zMax - i;
      }
    }

    /**
     * 判断当前节点是否在根节点的子树中
     * @param {Element} ele
     * @returns
     */
    function has(ele) {
      if (manualMaxDepth === 1) return ele.parentNode === domElement;

      let cur = ele;
      for (let i = 0; i < manualMaxDepth; i++) {
        if (cur.parentNode === domElement) return true;
        if (cur.parentNode === null) return false;
        cur = cur.parentNode;
      }

      return false;
    }
  }
}

export { CSS2DObject, CSS2DRenderer };
